'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Truck, Plus, Trash2, ChevronDown, ChevronUp, Save, CheckCircle2, AlertCircle, User, Building2, Phone, CreditCard, FileText, Package } from 'lucide-react';
import { SUPPLIER_CATEGORIES, SUPPLIER_TYPES, PAYMENT_TERMS_OPTIONS } from '../../components/suppliersData';
import Icon from '@/components/ui/AppIcon';


// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, subtitle, expanded, onToggle }: {
  icon: React.ElementType; title: string; subtitle?: string;
  expanded: boolean; onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50/50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
          <Icon size={16} className="text-orange-600" />
        </div>
        <div>
          <div className="text-sm font-bold text-gray-900">{title}</div>
          {subtitle && <div className="text-xs text-gray-400">{subtitle}</div>}
        </div>
      </div>
      {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
    </button>
  );
}

// ─── Form Field ───────────────────────────────────────────────────────────────

function Field({ label, required, children, hint }: { label: string; required?: boolean; children: React.ReactNode; hint?: string }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white";
const selectCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white";

// ─── Contact Row ──────────────────────────────────────────────────────────────

interface ContactRow {
  id: string;
  fullName: string;
  jobTitle: string;
  phone: string;
  email: string;
  isPrimary: boolean;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function NewSupplierContent() {
  const [sections, setSections] = useState({ basic: true, contacts: false, categories: false, bank: false, documents: false });
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Basic Info
  const [name, setName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [supplierType, setSupplierType] = useState('');
  const [regNumber, setRegNumber] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [country, setCountry] = useState('Uganda');
  const [district, setDistrict] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');

  // Categories
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Contacts
  const [contacts, setContacts] = useState<ContactRow[]>([
    { id: '1', fullName: '', jobTitle: '', phone: '', email: '', isPrimary: true },
  ]);

  // Bank
  const [bankName, setBankName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('30 Days');
  const [creditDays, setCreditDays] = useState('30');
  const [creditLimit, setCreditLimit] = useState('');

  const toggleSection = (key: keyof typeof sections) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const addContact = () => {
    setContacts(prev => [...prev, { id: Date.now().toString(), fullName: '', jobTitle: '', phone: '', email: '', isPrimary: false }]);
  };

  const removeContact = (id: string) => {
    setContacts(prev => prev.filter(c => c.id !== id));
  };

  const updateContact = (id: string, field: keyof ContactRow, value: string | boolean) => {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Supplier name is required';
    if (!supplierType) errs.supplierType = 'Supplier type is required';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Invalid email address';
    if (selectedCategories.length === 0) errs.categories = 'Select at least one category';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    setSaved(true);
    setTimeout(() => setSaved(false), 4000);
  };

  // Auto-generate supplier code
  const supplierCode = `SUP-${String(Math.floor(Math.random() * 90000) + 10000)}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <Link href="/suppliers" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={16} className="text-gray-500" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <Truck size={15} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Add New Supplier</h1>
              <p className="text-xs text-gray-400">Complete supplier registration and onboarding</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link href="/suppliers" className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              Cancel
            </Link>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors"
            >
              <Save size={14} />
              Save Supplier
            </button>
          </div>
        </div>
      </div>

      {saved && (
        <div className="mx-6 mt-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <CheckCircle2 size={16} className="text-green-600" />
          <span className="text-sm text-green-700 font-medium">Supplier saved successfully. Pending compliance review.</span>
          <Link href="/suppliers" className="ml-auto text-xs font-bold text-green-700 underline">View Suppliers →</Link>
        </div>
      )}

      <div className="p-6 max-w-3xl mx-auto space-y-4">

        {/* Supplier Code Preview */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="text-xs text-orange-600 font-semibold">Auto-generated Supplier Code:</div>
          <div className="text-sm font-black text-orange-700">{supplierCode}</div>
        </div>

        {/* Section A: Basic Information */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <SectionHeader icon={Building2} title="Basic Information" subtitle="Company details, registration, and contact" expanded={sections.basic} onToggle={() => toggleSection('basic')} />
          {sections.basic && (
            <div className="px-5 pb-5 space-y-4 border-t border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <Field label="Supplier Name" required>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ABC Feeds Uganda Ltd" className={inputCls} />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                </Field>
                <Field label="Trading Name">
                  <input type="text" value={tradingName} onChange={(e) => setTradingName(e.target.value)} placeholder="e.g. ABC Feeds" className={inputCls} />
                </Field>
                <Field label="Supplier Type" required>
                  <select value={supplierType} onChange={(e) => setSupplierType(e.target.value)} className={selectCls}>
                    <option value="">Select type...</option>
                    {SUPPLIER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {errors.supplierType && <p className="text-xs text-red-600 mt-1">{errors.supplierType}</p>}
                </Field>
                <Field label="Business Registration Number">
                  <input type="text" value={regNumber} onChange={(e) => setRegNumber(e.target.value)} placeholder="e.g. UG-REG-2024-0001" className={inputCls} />
                </Field>
                <Field label="TIN / Tax Number">
                  <input type="text" value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} placeholder="e.g. TIN-1001234567" className={inputCls} />
                </Field>
                <Field label="Country">
                  <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} className={inputCls} />
                </Field>
                <Field label="District / Region">
                  <input type="text" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. Kampala" className={inputCls} />
                </Field>
                <Field label="City / Town">
                  <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Kampala" className={inputCls} />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Physical Address">
                    <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. Plot 45, Industrial Area, Kampala" className={inputCls} />
                  </Field>
                </div>
                <Field label="General Email">
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="procurement@supplier.co.ug" className={inputCls} />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </Field>
                <Field label="General Phone">
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+256 700 000 000" className={inputCls} />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Website">
                    <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="www.supplier.co.ug" className={inputCls} />
                  </Field>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section B: Categories */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <SectionHeader icon={Package} title="Supplier Categories" subtitle="Select all applicable product/service categories" expanded={sections.categories} onToggle={() => toggleSection('categories')} />
          {sections.categories && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4">
              {errors.categories && (
                <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">
                  <AlertCircle size={12} />
                  {errors.categories}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {SUPPLIER_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${selectedCategories.includes(cat) ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {selectedCategories.length > 0 && (
                <div className="mt-3 text-xs text-gray-500">
                  {selectedCategories.length} categor{selectedCategories.length === 1 ? 'y' : 'ies'} selected
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section C: Contact Persons */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <SectionHeader icon={User} title="Contact Persons" subtitle="Add one or more supplier contacts" expanded={sections.contacts} onToggle={() => toggleSection('contacts')} />
          {sections.contacts && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
              {contacts.map((contact, idx) => (
                <div key={contact.id} className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-700">Contact {idx + 1}</span>
                      {contact.isPrimary && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">Primary</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {!contact.isPrimary && (
                        <button onClick={() => updateContact(contact.id, 'isPrimary', true)} className="text-xs text-orange-600 hover:underline">
                          Set Primary
                        </button>
                      )}
                      {contacts.length > 1 && (
                        <button onClick={() => removeContact(contact.id)} className="p-1 rounded-lg hover:bg-red-50 transition-colors">
                          <Trash2 size={12} className="text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Full Name" required>
                      <input type="text" value={contact.fullName} onChange={(e) => updateContact(contact.id, 'fullName', e.target.value)} placeholder="e.g. Mukasa David" className={inputCls} />
                    </Field>
                    <Field label="Job Title">
                      <input type="text" value={contact.jobTitle} onChange={(e) => updateContact(contact.id, 'jobTitle', e.target.value)} placeholder="e.g. Sales Manager" className={inputCls} />
                    </Field>
                    <Field label="Phone">
                      <input type="tel" value={contact.phone} onChange={(e) => updateContact(contact.id, 'phone', e.target.value)} placeholder="+256 700 000 000" className={inputCls} />
                    </Field>
                    <Field label="Email">
                      <input type="email" value={contact.email} onChange={(e) => updateContact(contact.id, 'email', e.target.value)} placeholder="contact@supplier.co.ug" className={inputCls} />
                    </Field>
                  </div>
                </div>
              ))}
              <button onClick={addContact} className="flex items-center gap-2 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors">
                <Plus size={13} />
                Add Another Contact
              </button>
            </div>
          )}
        </div>

        {/* Section D: Bank & Payment */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <SectionHeader icon={CreditCard} title="Bank & Payment Information" subtitle="Financial details and payment terms" expanded={sections.bank} onToggle={() => toggleSection('bank')} />
          {sections.bank && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4">
              <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-4">
                <AlertCircle size={12} />
                Banking information is restricted to authorized users only.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Bank Name">
                  <input type="text" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. Stanbic Bank Uganda" className={inputCls} />
                </Field>
                <Field label="Account Name">
                  <input type="text" value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="e.g. ABC Feeds Uganda Ltd" className={inputCls} />
                </Field>
                <Field label="Account Number">
                  <input type="text" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="e.g. 9030012345678" className={inputCls} />
                </Field>
                <Field label="Payment Terms">
                  <select value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className={selectCls}>
                    {PAYMENT_TERMS_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Credit Days">
                  <input type="number" value={creditDays} onChange={(e) => setCreditDays(e.target.value)} min={0} className={inputCls} />
                </Field>
                <Field label="Credit Limit (UGX)">
                  <input type="number" value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} min={0} placeholder="e.g. 50000000" className={inputCls} />
                </Field>
              </div>
            </div>
          )}
        </div>

        {/* Section E: Documents */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <SectionHeader icon={FileText} title="Documents" subtitle="Upload compliance and registration documents" expanded={sections.documents} onToggle={() => toggleSection('documents')} />
          {sections.documents && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4">
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                <FileText size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-500">Upload supplier documents</p>
                <p className="text-xs text-gray-400 mt-1">Certificate of Incorporation, Trading License, TIN Certificate, etc.</p>
                <button className="mt-4 px-4 py-2 text-xs font-semibold text-orange-600 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors">
                  Browse Files
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end gap-3 pb-6">
          <Link href="/suppliers" className="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            Cancel
          </Link>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors"
          >
            <Save size={14} />
            Save & Submit for Review
          </button>
        </div>
      </div>
    </div>
  );
}
